import { EXPORT_MAX_DIMENSION_PX, EXPORT_MAX_PIXELS, calculateExportLayout } from '@bw/core';
import type { Bounds, ExportLayout, PlacedBrick, WallModel } from '@bw/core';

/**
 * SPECIFICATION.md clause 14.5, with the caps exposed as a choice: the
 * specification's values are the default and the ceiling (design document E3).
 */
export type RasterResolution = 'screen' | 'print' | 'maximum';

export const RESOLUTION_PX_PER_MM: Record<RasterResolution, number> = {
  screen: 1,
  print: 2, // the specification's preferred resolution
  maximum: 4,
};

export const CANVAS_BACKGROUND = '#F4F1EA';

export function rasterLayout(bounds: Bounds, resolution: RasterResolution = 'print'): ExportLayout {
  return calculateExportLayout(bounds, {
    pixelsPerMm: RESOLUTION_PX_PER_MM[resolution],
    maxDimensionPx: EXPORT_MAX_DIMENSION_PX,
    maxPixels: EXPORT_MAX_PIXELS,
  });
}

function darken(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const factor = 1 - amount;
  const channel = (offset: number): string =>
    Math.max(0, Math.min(255, Math.round(Number.parseInt(value.slice(offset, offset + 2), 16) * factor)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(0)}${channel(2)}${channel(4)}`.toUpperCase();
}

export interface RasterTarget {
  fillStyle: string;
  fillRect: (x: number, y: number, w: number, h: number) => void;
}

/**
 * Paint the wall alone — never the grid, selection outlines, previews or
 * measurement — fitted independently of the viewport (clause 14.5).
 */
export function paintRaster(
  target: RasterTarget,
  model: WallModel,
  colourOf: (brick: PlacedBrick) => string,
  layout: ExportLayout,
  background = CANVAS_BACKGROUND,
): void {
  target.fillStyle = background;
  target.fillRect(0, 0, layout.width, layout.height);

  for (const brick of model.bricks) {
    const leftF = (brick.x - layout.leftWorld) * layout.scale;
    const topF = (layout.topWorld - brick.top) * layout.scale;
    const rightF = (brick.right - layout.leftWorld) * layout.scale;
    const bottomF = (layout.topWorld - brick.y) * layout.scale;

    const left = Math.max(0, Math.min(layout.width - 1, Math.round(leftF)));
    const top = Math.max(0, Math.min(layout.height - 1, Math.round(topF)));
    const right = Math.max(left + 1, Math.min(layout.width, Math.round(rightF)));
    const bottom = Math.max(top + 1, Math.min(layout.height, Math.round(bottomF)));
    const pixelWidth = right - left;
    const pixelHeight = bottom - top;
    const colour = colourOf(brick);

    if (pixelWidth >= 3 && pixelHeight >= 3) {
      const border = Math.min(
        Math.max(1, Math.min(4, Math.round(layout.scale))),
        Math.floor((pixelWidth - 1) / 2),
        Math.floor((pixelHeight - 1) / 2),
      );
      target.fillStyle = darken(colour, 0.48);
      target.fillRect(left, top, pixelWidth, pixelHeight);
      target.fillStyle = colour;
      target.fillRect(left + border, top + border, pixelWidth - border * 2, pixelHeight - border * 2);
    } else {
      target.fillStyle = colour;
      target.fillRect(left, top, pixelWidth, pixelHeight);
    }
  }
}

export interface RasterResult {
  readonly bytes: Uint8Array;
  readonly width: number;
  readonly height: number;
}

/** Render to PNG bytes through an OffscreenCanvas, for the export worker. */
export async function renderWallPng(
  model: WallModel,
  colourOf: (brick: PlacedBrick) => string,
  resolution: RasterResolution = 'print',
): Promise<RasterResult> {
  const bounds = model.bounds();
  if (bounds === null) throw new Error('Lay at least one brick before exporting a PNG');
  const layout = rasterLayout(bounds, resolution);
  if (typeof OffscreenCanvas === 'undefined') {
    throw new Error('This browser cannot render a PNG off the main thread');
  }
  const canvas = new OffscreenCanvas(layout.width, layout.height);
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Canvas 2D is unavailable');
  paintRaster(context as unknown as RasterTarget, model, colourOf, layout);
  const blob = await canvas.convertToBlob({ type: 'image/png' });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width: layout.width, height: layout.height };
}
